import {
  FetcherFactory,
  ImportPlan,
  ImportPlanAlbum as GenericPlanAlbum,
} from 'helpers/import-types';
import { mimeFromFileName } from 'helpers/import-mime';
import { API_ROUTES } from 'networking/api-routes';
import {
  IcloudController,
  IcloudPhotoRaw,
} from 'networking/controllers/icloud-controller';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type IcloudFile = {
  id: string; // photoGuid
  name: string;
  mimeType: string;
  size?: number;
  modifiedTime?: string;
  createdTime?: string;
  takenAt?: string;
  checksum: string;
  isVideo: boolean;
  // The shared album the file belongs to, for signing its download URL.
  token: string;
  host: string;
};

export type IcloudFolder = {
  id: string; // the shared-album token
  name: string;
  createdTime?: string;
  // Carried through the plan so the runner can fetch signed asset URLs.
  token: string;
  host: string;
};

export type IcloudPlanAlbum = GenericPlanAlbum<IcloudFile, IcloudFolder>;

export type IcloudPlan = ImportPlan<IcloudFile, IcloudFolder>;

// Signed CDN URLs expire (~1h); resolve them lazily in small batches.
const ASSET_URL_BATCH = 20;

// ---------------------------------------------------------------------------
// Link parsing
// ---------------------------------------------------------------------------

// Accepts https://www.icloud.com/sharedalbum/#B0abcDEF… (with or without www /
// trailing junk). The album token is the URL fragment.
export const parseICloudSharedAlbumLink = (input: string): string | null => {
  const match = input
    .trim()
    .match(/icloud\.com\/sharedalbum\/?#([A-Za-z0-9_-]{6,64})/i);
  return match ? match[1] : null;
};

// ---------------------------------------------------------------------------
// Planning
// ---------------------------------------------------------------------------

// For videos Apple's derivatives mix poster frames and the actual clip — the
// clip is the biggest payload, so pick by file size. For photos pick the
// largest width (capped at 2048px by Apple).
const bestDerivative = (photo: IcloudPhotoRaw) => {
  const derivatives = photo.derivatives.filter((d) => d.checksum);
  if (derivatives.length === 0) return null;
  const isVideo = photo.media_asset_type === 'video';
  return derivatives.reduce((best, d) => {
    if (isVideo) return d.file_size > best.file_size ? d : best;
    if (d.width !== best.width) return d.width > best.width ? d : best;
    return d.file_size > best.file_size ? d : best;
  });
};

const photoToFile = (
  photo: IcloudPhotoRaw,
  token: string,
  host: string,
): IcloudFile | null => {
  const derivative = bestDerivative(photo);
  if (!derivative) return null;
  const isVideo = photo.media_asset_type === 'video';
  // Live Photos surface as a photo with an extra (small) video derivative —
  // media_asset_type stays 'photo', so we import the still only.
  const base = photo.caption || `IMG_${photo.photo_guid.slice(0, 8)}`;
  const ext = isVideo ? '.mp4' : '.jpg';
  const name = /\.[a-z0-9]{2,4}$/i.test(base) ? base : `${base}${ext}`;
  return {
    id: photo.photo_guid,
    name,
    mimeType: isVideo ? 'video/mp4' : 'image/jpeg',
    size: derivative.file_size || undefined,
    // date_created is when the photo was taken; batch_date_created is when
    // it was posted to the shared album.
    takenAt: photo.date_created || undefined,
    createdTime: photo.batch_date_created || undefined,
    checksum: derivative.checksum,
    isVideo,
    token,
    host,
  };
};

/**
 * Fetch the shared album's feed and plan it as ONE album (shared albums are
 * flat — no subfolders) named after the stream.
 */
export const planIcloudImport = async (link: string): Promise<IcloudPlan> => {
  const token = parseICloudSharedAlbumLink(link);
  if (!token) {
    throw new Error('That link doesn’t look like an iCloud Shared Album link.');
  }

  const stream = await IcloudController.getStream(token);
  const files = (stream.photos || [])
    .map((photo) => photoToFile(photo, token, stream.host))
    .filter((file): file is IcloudFile => file != null);

  const earliest = (stream.photos || [])
    .map((p) => p.batch_date_created || p.date_created)
    .filter(Boolean)
    .sort()[0];

  const folder: IcloudFolder = {
    id: token,
    name: stream.stream_name,
    createdTime: earliest || undefined,
    token,
    host: stream.host,
  };

  return {
    rootName: stream.stream_name,
    albums: files.length > 0 ? [{ folder, files }] : [],
  };
};

// ---------------------------------------------------------------------------
// Downloading
// ---------------------------------------------------------------------------

// The iCloud CDN may or may not send CORS headers; fall back to the Rails
// streaming proxy when the direct fetch is blocked.
const downloadAsset = async (url: string): Promise<Blob> => {
  try {
    const direct = await fetch(url);
    if (!direct.ok) throw new Error(`iCloud CDN ${direct.status}`);
    return await direct.blob();
  } catch (err) {
    if (!(err instanceof TypeError)) throw err; // real HTTP error — bubble up
    const proxied = await fetch(
      `${API_ROUTES.ICLOUD_DOWNLOAD}?url=${encodeURIComponent(url)}`,
    );
    if (!proxied.ok) throw new Error(`iCloud proxy ${proxied.status}`);
    return proxied.blob();
  }
};

/**
 * Downloads files for one run. Signed CDN URLs expire (~1h), so they are
 * requested lazily, ASSET_URL_BATCH at a time in run order, and a batch is
 * re-signed once if a download fails mid-run.
 */
export const makeIcloudFetcher: FetcherFactory<IcloudFile> = (files) => {
  const urls = new Map<string, string>(); // checksum → signed URL
  const position = new Map(files.map((file, index) => [file.id, index]));

  const signBatch = async (from: number, again = false) => {
    const batch = files
      .slice(from, from + ASSET_URL_BATCH)
      .filter((file) => again || !urls.has(file.checksum));
    if (batch.length === 0) return;
    const { token, host } = batch[0];
    const signed = await IcloudController.getAssetUrls(
      token,
      host,
      batch.map((file) => file.id),
    );
    Object.entries(signed).forEach(([checksum, url]) =>
      urls.set(checksum, url),
    );
  };

  return async (file) => {
    const index = position.get(file.id) ?? 0;
    if (!urls.has(file.checksum)) await signBatch(index);
    let url = urls.get(file.checksum);
    if (!url) throw new Error('No download URL for asset');

    let blob: Blob;
    try {
      blob = await downloadAsset(url);
    } catch {
      await signBatch(index, true);
      url = urls.get(file.checksum);
      if (!url) throw new Error('No download URL for asset');
      blob = await downloadAsset(url);
    }

    // The CDN path carries the real extension (.JPG/.MOV/...) — prefer it
    // over the provisional plan-time guess.
    const urlMime = mimeFromFileName(new URL(url).pathname);
    const mimeType =
      urlMime !== 'application/octet-stream' ? urlMime : file.mimeType;
    const posted = file.createdTime ? Date.parse(file.createdTime) : NaN;
    return new File([blob], file.name, {
      type: mimeType,
      lastModified: Number.isFinite(posted) ? posted : Date.now(),
    });
  };
};
